/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useMemo, useState } from 'react';

import user from 'js/lib/user';

import { Button, Tag, Typography } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import TrackedButton from 'bundles/page/components/TrackedButton';
import SeoSkillsLinkTag from 'bundles/seo/components/SeoSkillsLinkTag';
import useIsMobile from 'bundles/unified-common/hooks/useIsMobile';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { useMWebStreamlinedXDP } from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

import _t from 'i18n!nls/xdp';

const CHARACTER_LIMIT = {
  DESKTOP: 180,
  MOBILE: 120,
};

const styles = {
  container: (theme: Theme) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(16)};
  `,
  button: css`
    padding: 0;
    margin-left: 0;

    :hover {
      background: transparent;
    }
  `,
  hideSkill: css`
    display: none;
  `,
};

export const getLastIndex = (skills: string[] | null | undefined, characterLimit: number) => {
  if (!skills?.length) {
    return -1;
  }

  let remainingCharacterCount = characterLimit;

  const index = skills.findIndex((skill) => {
    remainingCharacterCount -= skill.length;
    return remainingCharacterCount < 0;
  });

  if (index === -1) {
    return skills.length - 1;
  } else if (index === 0) {
    return index;
  }

  return index - 1;
};

type Props = {
  title?: string;
  skills?: string[] | null;
  disableCTA?: boolean;
};

const Skills = ({ skills, title, disableCTA }: Props) => {
  const [isShowingAll, setIsShowingAll] = useState(false);
  const { subfolderLocaleCode } = usePageInfo();

  const isMobile = useIsMobile();

  const toggleSkills = useCallback(() => {
    setIsShowingAll((previousState) => !previousState);
  }, []);

  const characterLimit = isMobile ? CHARACTER_LIMIT.MOBILE : CHARACTER_LIMIT.DESKTOP;

  const lastIndexToShow = useMemo(() => {
    return getLastIndex(skills, characterLimit);
  }, [skills, characterLimit]);

  const isMWebStreamlinedXDP = useMWebStreamlinedXDP();

  if (!skills?.length) {
    return null;
  }

  // Add links for each skill only if logged out
  const enableSkillsLinking = !user.isAuthenticatedUser() && !disableCTA && !subfolderLocaleCode;

  return (
    <Subsection
      title={<Typography variant="h2semibold">{title ?? _t("Skills you'll gain")}</Typography>}
      spacing={isMWebStreamlinedXDP ? 12 : 24}
    >
      <ul css={styles.container}>
        {skills.map((skill, index) => {
          return (
            <li key={skill} css={!isShowingAll && index > lastIndexToShow ? styles.hideSkill : undefined}>
              {enableSkillsLinking ? <SeoSkillsLinkTag skill={skill} /> : <Tag>{skill}</Tag>}
            </li>
          );
        })}
        {!disableCTA && skills.length - 1 > lastIndexToShow && (
          <li>
            <Button
              variant="ghost"
              edgeAlign="start"
              size="small"
              onClick={toggleSkills}
              css={styles.button}
              component={TrackedButton}
              trackingName="overview_skills_toggle"
              requireFullyVisible={false}
              withVisibilityTracking={false}
            >
              {isShowingAll ? _t('View less skills') : _t('View all skills')}
            </Button>
          </li>
        )}
      </ul>
    </Subsection>
  );
};

export default Skills;
